import { FC } from "react";
import { ProductLabel } from "src/types/graphql.d";
import {
  PriceWrapper,
  RegularPrice,
  StyledBrandName,
  StyledBrandNameLink,
  StyledProductHeading,
  StyledProductName,
} from "./ProductInfo.styled";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { locale } from "src/config/app-config";
import { Price } from "src/atoms/Price";
import PriceInfo from "src/molecules/ProductInfo/PriceInfo";
import { Flash } from "src/icons/Flash";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { localeFix } from "src/utils/genderLocale/genderLocale";

interface Props {
  englishGenderId?: string;
  price: number;
  productName: string;
  labels: ProductLabel[];
  regularPrice: number;
  lowestPrice?: number | null;
  brandName: string;
  brandSlug: string;
  promoCode?: string;
}

const ProductInfo: FC<Props> = ({
  price,
  productName,
  regularPrice,
  lowestPrice,
  brandName,
  brandSlug,
  englishGenderId,
  promoCode,
}) => {
  const { promotionContextState } = usePromotionContext();
  const { currentShopType } = useShopTypeContext();

  return (
    <header>
      <StyledProductHeading as="h1" variant="h2">
        {brandSlug !== undefined ? (
          <StyledBrandNameLink
            data-testid="product-info-brand-name"
            href={`${getI18nRoute({
              route: `/brands`,
              locale,
            })}/${brandSlug}${getI18nRoute({
              route: `/${localeFix({ gender: currentShopType })}`,
              locale,
            })}`}
            prefetch={false}
          >
            {brandName}
          </StyledBrandNameLink>
        ) : (
          <StyledBrandName data-testid="product-info-brand-name">
            {brandName}
          </StyledBrandName>
        )}
        &nbsp;
        <StyledProductName>{productName}</StyledProductName>
      </StyledProductHeading>
      <PriceWrapper>
        <p data-testid="product-info-price">
          <Price price={price} />
        </p>
        <RegularPrice data-testid="product-info-regular-price">
          <Price price={regularPrice} />
          {"*"}
        </RegularPrice>
        {promoCode && !promotionContextState.hasPromotionEnded ? (
          <span className="promo-code">
            <Flash />
            {promoCode}
          </span>
        ) : null}
      </PriceWrapper>
      <PriceInfo lowestPrice={lowestPrice} />
    </header>
  );
};

export default ProductInfo;
